import { Component, Input, Output, EventEmitter, signal, computed, OnChanges, SimpleChanges, OnInit, OnDestroy, Injector, forwardRef } from '@angular/core';

import { ReactiveFormsModule, FormControl, ControlValueAccessor, NgControl, NG_VALUE_ACCESSOR, FormGroupDirective, NgForm } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ErrorStateMatcher } from '@angular/material/core';

@Component({
    selector: 'app-autocomplete-field',
    imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    MatIconModule,
    MatButtonModule
],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => AutocompleteFieldComponent),
            multi: true
        }
    ],
    template: `
    <mat-form-field class="w-100">
      <mat-label>{{ label }}</mat-label>
      <input
        type="text"
        matInput
        [attr.aria-label]="label"
        [placeholder]="placeholder"
        [matAutocomplete]="auto"
        [formControl]="control"
        [errorStateMatcher]="errorStateMatcher"
        (input)="filterItems($event)"
        (blur)="handleBlur()"
        />
        @if (control.value && !control.disabled && !isLoading) {
          <button
            matSuffix
            mat-icon-button
            aria-label="Clear"
            (click)="clear($event)"
            >
            <mat-icon>close</mat-icon>
          </button>
        }
        @if (isLoading) {
          <mat-icon matSuffix class="loading-spinner">refresh</mat-icon>
        }
        <mat-autocomplete #auto="matAutocomplete" [displayWith]="getDisplayValue">
          @for (item of filteredItems(); track trackByFn($index, item)) {
            <mat-option [value]="item">
              {{ displayFn ? displayFn(item) : getDefaultDisplay(item) }}
            </mat-option>
          }
        </mat-autocomplete>
    
        <!-- Error messages are now shown by MatFormField based on the errorStateMatcher -->
        <mat-error>
          @if (ngControl?.control?.hasError('required')) {
            <span>{{ errorMessage || 'Ce champ est requis' }}</span>
          }
        </mat-error>
      </mat-form-field>
    `,
    styles: [`
    :host {
      display: block;
      width: 100%;
    }

    button[matSuffix] {
      margin-right: 8px;
    }

    mat-error {
      font-size: 12px;
      margin-top: 4px;
    }

    .loading-spinner {
      animation: spin 1s linear infinite;
    }

    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
  `]
})
export class AutocompleteFieldComponent<T = any> implements ControlValueAccessor, OnChanges, OnInit, OnDestroy {
  @Input() label = '';
  @Input() placeholder = '';
  @Input() items: T[] = [];
  @Input() displayFn?: (item: T) => string;
  @Input() filterFn?: (item: T, searchText: string) => boolean;
  @Input() showError = true;
  @Input() errorMessage = '';
  @Input() clearDependents?: () => void;
  @Input() isLoading = false;
  @Input() loadingText = 'Chargement...';
  @Output() valueWritten = new EventEmitter<T | null>();

  // Internal FormControl for the autocomplete
  control = new FormControl<T | null>(null);
  
  // NgControl reference for validation
  ngControl: NgControl | null = null;
  
  // Subject to manage unsubscription
  private destroy$ = new Subject<void>();
  
  // ControlValueAccessor callbacks
  onChange: (value: T | null) => void = () => {};
  onTouched: () => void = () => {};
  
  searchText = signal('');
  itemsSignal = signal<T[]>([]);

  errorStateMatcher: ErrorStateMatcher = {
    isErrorState: (control: FormControl | null, form: FormGroupDirective | NgForm | null): boolean => {
      const isInvalidAndTouched = !!(this.ngControl?.control?.invalid && this.ngControl?.control?.touched);
      return isInvalidAndTouched && this.showError;
    }
  };
  
  filteredItems = computed(() => {
    const search = this.searchText().toLowerCase();
    const items = this.itemsSignal(); // Use internal signal
    
    if (!search || search === '') {
      return items;
    }
    
    if (this.filterFn) {
      return items.filter((item: T) => this.filterFn!(item, search));
    }
    
    return items.filter((item: T) => {
      const display = this.getDefaultDisplay(item);
      return display.toLowerCase().includes(search);
    });
  });

  constructor(private injector: Injector) {}

  
  // ControlValueAccessor methods
  writeValue(value: T | null): void {
    this.control.setValue(value, { emitEvent: false });
  }

  registerOnChange(fn: (value: T | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.control.disable({ emitEvent: false });
    } else {
      this.control.enable({ emitEvent: false });
    }
  }
  
  ngOnInit(): void {
    // Initialize itemsSignal with current items
    this.itemsSignal.set(this.items || []);
    
    // Get the NgControl that wraps this component
    this.ngControl = this.injector.get(NgControl, null);
    
    if (this.ngControl) {
      // Set this component as the value accessor
      this.ngControl.valueAccessor = this;
    }

    // Setup value change tracking - propagate internal control changes to parent
    this.control.valueChanges.pipe(
      takeUntil(this.destroy$)
    ).subscribe(value => {
      this.onChange(value);
      this.valueWritten.emit(value);
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['items']) {
      // Update internal signal when @Input items changes
      this.itemsSignal.set(this.items || []);
    }
  }


  filterItems(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.searchText.set(target.value);
  }

  handleBlur(): void {
    // Mark both internal and parent control as touched when user interacts and leaves the field
    this.control.markAsTouched();
    this.ngControl?.control?.markAsTouched();
    this.onTouched();
  }

  clear(event: Event): void {
    event.stopPropagation();
    this.control.setValue(null);
    this.searchText.set('');
    this.onTouched(); // Mark the control as touched
    // Note: valueWritten.emit and onChange will be called automatically by the subscription
    if (this.clearDependents) {
      this.clearDependents();
    }
  }

  getDisplayValue = (item: T | null): string => {
    if (!item) return '';
    return this.displayFn ? this.displayFn(item) : this.getDefaultDisplay(item);
  };

  getDefaultDisplay(item: T | null): string {
    if (!item) return '';
    
    // Common pattern for items with libelle property
    if (typeof item === 'object' && 'libelle' in item) {
      return (item as any).libelle;
    }
    
    // Fallback to string representation
    return String(item);
  }

  trackByFn(index: number, item: T): any {
    if (item && typeof item === 'object' && 'id' in item) {
      return (item as any).id;
    }
    return index;
  }

}
